'use client';

import { X } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import type { UniversityGalleryItem } from '../../../lib/types';

type Props = {
  items: UniversityGalleryItem[];
};

export function UniversityGalleryClient({ items }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const activeItem = activeIndex === null ? null : items[activeIndex];

  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        {items.map((item, index) => (
          <button
            key={item.image}
            type="button"
            onClick={() => setActiveIndex(index)}
            className="group overflow-hidden rounded-3xl border border-slate-700/40 bg-slate-900/35 text-left shadow-[0_20px_60px_-35px_rgba(14,116,144,0.4)] transition hover:-translate-y-0.5 hover:border-main/60 hover:bg-slate-900/45 hover:shadow-[0_28px_80px_-40px_rgba(250,204,21,0.35)] dark:border-white/10 dark:bg-white/5 dark:hover:border-main/45 dark:hover:bg-white/10"
          >
            <div className="relative h-56 overflow-hidden bg-slate-900">
              <Image
                src={item.image}
                alt={item.alt}
                fill
                className="object-cover transition duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 33vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-900/10 to-transparent opacity-80 transition group-hover:opacity-60" />
              <div className="absolute bottom-3 left-3 rounded-full border border-white/20 bg-slate-950/50 px-3 py-1 text-xs font-bold text-main backdrop-blur">
                Открыть фото
              </div>
            </div>
            <div className="p-4">
              <h3 className="text-base font-black text-main transition group-hover:text-yellow-300">
                {item.title}
              </h3>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-sky-100/75">
                {item.location}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-200/90 transition group-hover:text-white">
                {item.description}
              </p>
            </div>
          </button>
        ))}
      </div>

      {activeItem ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveIndex(null)}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setActiveIndex(null);
            }}
            className="absolute right-4 top-4 rounded-2xl border border-white/20 bg-white/10 p-3 text-white transition hover:bg-white/20"
            aria-label="Закрыть фотографию"
          >
            <X className="h-5 w-5" />
          </button>

          <div
            className="w-full max-w-5xl overflow-hidden rounded-[28px] border border-white/15 bg-slate-950 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative h-[72vh] max-h-[760px] min-h-[320px] bg-slate-900">
              <Image
                src={activeItem.image}
                alt={activeItem.alt}
                fill
                className="object-contain"
                sizes="100vw"
                priority
              />
            </div>
            <div className="border-t border-white/10 p-4 sm:p-5">
              <h3 className="text-lg font-black text-main">{activeItem.title}</h3>
              <p className="mt-1 text-sm text-slate-300">{activeItem.location}</p>
              <p className="mt-2 text-sm leading-6 text-slate-100/90">{activeItem.description}</p>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
